import React, { useState, useImperativeHandle, useRef } from 'react'
import { StyleProp, TextStyle, TouchableOpacity, View, ViewStyle } from 'react-native'
import Video, {
  OnBufferData,
  OnLoadData,
  OnPlaybackStateChangedData,
  OnProgressData,
  OnVideoErrorData,
  VideoRef,
} from 'react-native-video'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon } from '@src/components/base'
import { LoadingIndicator } from '../LoadingIndicator'
import { AudioPlayerSlider } from './AudioPlayerSlider'

interface AudioSource {
  uri: string
}

interface AudioPlayerProps {
  source: AudioSource
  style?: StyleProp<ViewStyle>
  onReady?: (videoRef: VideoRef) => void
  onPlay?: () => void
  onPause?: () => void
}

export const AudioPlayer = React.forwardRef<VideoRef | null, AudioPlayerProps>(
  ({ source, style, onReady = () => 0, onPlay = () => 0, onPause = () => 0 }, forwardedRef) => {
    const styles = useStyleSheet(themedStyles)
    const videoRef = useRef<VideoRef>(null)
    const [isLoaded, setIsLoaded] = useState(false)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isBuffering, setIsBuffering] = useState(false)
    const [position, setPosition] = useState(0)
    const [duration, setDuration] = useState(0)

    const showBufferingIndicator = isBuffering && isLoaded

    useImperativeHandle(forwardedRef, () => videoRef.current as VideoRef, [])

    const handleLoad = (data: OnLoadData) => {
      setIsLoaded(true)
      setDuration(data.duration)
      if (videoRef.current) {
        onReady(videoRef.current)
      }
    }

    const handleProgress = (data: OnProgressData) => {
      setPosition(data.currentTime)
    }

    const handleBuffer = (data: OnBufferData) => {
      setIsBuffering(data.isBuffering)
    }

    const handlePlaybackStateChanged = (data: OnPlaybackStateChangedData) => {
      if (data.isSeeking) {
        return
      }

      const wasPlaying = isPlaying
      setIsPlaying(data.isPlaying)

      if (data.isPlaying && !wasPlaying) {
        onPlay()
      } else if (!data.isPlaying && wasPlaying && !isBuffering) {
        onPause()
      }
    }

    const handleError = (error: OnVideoErrorData) => {
      console.error('failed to load the sound', error)
      setIsLoaded(false)
    }

    const togglePlayPause = () => {
      if (!videoRef.current) {
        return
      }

      if (isPlaying) {
        videoRef.current.pause()
      } else if (Math.round(position) === Math.round(duration)) {
        videoRef.current.seek(0)
        videoRef.current.resume()
        setIsPlaying(true)
      } else {
        videoRef.current.resume()
      }
    }

    return (
      <View style={[styles.container, style]}>
        <Video
          ref={videoRef}
          source={source}
          paused={!isPlaying}
          onLoad={handleLoad}
          onProgress={handleProgress}
          onBuffer={handleBuffer}
          onPlaybackStateChanged={handlePlaybackStateChanged}
          onError={handleError}
          style={{ width: 0, height: 0 }} // Hidden video component for audio playback
          progressUpdateInterval={250}
          ignoreSilentSwitch="ignore"
        />
        {!isLoaded || showBufferingIndicator ? (
          <TouchableOpacity
            style={
              showBufferingIndicator ? styles.playPauseButton : styles.loadingIndicatorContainer
            }
            onPress={() => {
              if (showBufferingIndicator && videoRef.current) {
                videoRef.current.pause()
              }
            }}
            accessibilityLabel={showBufferingIndicator ? 'Buffering' : 'Loading'}
            disabled={!showBufferingIndicator}
          >
            <LoadingIndicator
              color={
                ((showBufferingIndicator ? styles.icon : styles.loadingIndicator) as TextStyle)
                  .color
              }
            />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={styles.playPauseButton}
            onPress={togglePlayPause}
            accessibilityLabel={isPlaying ? 'Pause' : 'Play'}
          >
            <Icon name={isPlaying ? 'pause' : 'play'} weight="fill" style={styles.icon} />
          </TouchableOpacity>
        )}
        <AudioPlayerSlider
          duration={duration}
          position={position}
          playableDuration={position}
          onSeek={(position) => {
            if (!videoRef.current) {
              return
            }

            videoRef.current.seek(position)
          }}
        />
      </View>
    )
  },
)

const themedStyles = StyleService.create({
  container: {
    width: '100%',
    margin: 24,
    padding: 16,
    paddingBottom: 8,
    backgroundColor: 'theme.surface.base2',
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  playPauseButton: {
    backgroundColor: 'theme.primary.base',
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginTop: 9,
  },
  loadingIndicatorContainer: {
    width: 30,
    height: 30,
    alignSelf: 'flex-start',
    marginTop: 9,
  },
  loadingIndicator: {
    color: 'theme.text.primary',
  },
  icon: {
    color: 'theme.solid.white',
    width: 12,
    height: 12,
  },
})
